"use client";

import { useTranslations } from "next-intl";
import { SectionShell, EditorialTitle, GradientText, AnimatedBar, RevealOnScroll } from "./_primitives";

export default function TransparencySection() {
    const t = useTranslations();

    const splits = [
        {
            label: t("joinInstructor_transp_split1_label"),
            title: t("joinInstructor_transp_split1_title"),
            desc: t("joinInstructor_transp_split1_desc"),
            pct: 30,
            tone: "neutral",
        },
        {
            label: t("joinInstructor_transp_split2_label"),
            title: t("joinInstructor_transp_split2_title"),
            desc: t("joinInstructor_transp_split2_desc"),
            pct: 20,
            tone: "neutral",
        },
        {
            label: t("joinInstructor_transp_split3_label"),
            title: t("joinInstructor_transp_split3_title"),
            desc: t("joinInstructor_transp_split3_desc"),
            pct: 50,
            tone: "highlight",
        },
    ];

    return (
        <SectionShell label={t("joinInstructor_transp_marker")}>
            <div className="grid lg:grid-cols-2 gap-8 sm:gap-10 items-start mb-12">
                <RevealOnScroll>
                    <p className="text-[11px] uppercase tracking-[0.28em] text-primary mb-6">
                        {t("joinInstructor_transp_eyebrow")}
                    </p>
                    <EditorialTitle className="text-4xl sm:text-5xl md:text-6xl">
                        {t("joinInstructor_transp_title_part1")}
                        <br />
                        <GradientText>{t("joinInstructor_transp_title_emphasis")}</GradientText>{" "}
                        {t("joinInstructor_transp_title_part2")}.
                    </EditorialTitle>
                </RevealOnScroll>
                <RevealOnScroll delay={0.1}>
                    <p
                        className="text-gray-500 dark:text-white/55 italic text-lg lg:text-right"
                        style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
                    >
                        {t("joinInstructor_transp_subtitle")}
                    </p>
                </RevealOnScroll>
            </div>

            <div className="grid lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2 space-y-5">
                    {splits.map((s, i) => (
                        <RevealOnScroll key={i} delay={i * 0.1}>
                            <div
                                className={`relative rounded-2xl p-6 ${
                                    s.tone === "highlight"
                                        ? "border border-primary/40 bg-gradient-to-br from-primary/15 via-transparent to-transparent"
                                        : "border border-gray-200 dark:border-white/10 bg-gray-50 dark:bg-white/[0.03]"
                                }`}
                            >
                                <div className="flex justify-between items-start mb-3">
                                    <div>
                                        <div className="text-[10px] uppercase tracking-[0.25em] text-gray-400 dark:text-white/40 mb-2 font-mono">
                                            {s.label}
                                        </div>
                                        <h3
                                            className={`text-lg sm:text-xl md:text-2xl font-bold ${
                                                s.tone === "highlight" ? "text-primary" : "text-gray-900 dark:text-white"
                                            }`}
                                            style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
                                        >
                                            {s.title}
                                        </h3>
                                    </div>
                                    <div
                                        className={`text-4xl sm:text-5xl md:text-6xl font-bold leading-none ${
                                            s.tone === "highlight" ? "text-primary" : "text-gray-600 dark:text-white/70"
                                        }`}
                                        style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
                                    >
                                        {s.pct}
                                        <span className="text-2xl ml-1">%</span>
                                    </div>
                                </div>
                                <p className="text-gray-500 dark:text-white/55 text-sm leading-relaxed mb-4">{s.desc}</p>
                                <AnimatedBar
                                    percent={s.pct * 2}
                                    from={s.tone === "highlight" ? "from-[#FF7A1A]" : "from-gray-300 dark:from-white/30"}
                                    to={s.tone === "highlight" ? "to-[#E51578]" : "to-gray-300 dark:to-white/30"}
                                    delay={0.3 + i * 0.15}
                                />
                            </div>
                        </RevealOnScroll>
                    ))}
                </div>

                <RevealOnScroll delay={0.2}>
                    <div className="h-full rounded-2xl border border-gray-200 dark:border-white/10 bg-gray-50 dark:bg-white/[0.03] p-7 flex flex-col justify-between">
                        <div>
                            <div className="text-[10px] uppercase tracking-[0.25em] text-gray-400 dark:text-white/40 mb-4 font-mono">
                                {t("joinInstructor_transp_promise_eyebrow")}
                            </div>
                            <h3
                                className="text-xl sm:text-2xl md:text-3xl text-gray-900 dark:text-white font-bold mb-5 leading-tight"
                                style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
                            >
                                {t("joinInstructor_transp_promise_title_part1")}{" "}
                                <GradientText>
                                    {t("joinInstructor_transp_promise_title_emphasis")}
                                </GradientText>
                                .
                            </h3>
                            <p className="text-gray-500 dark:text-white/55 text-sm leading-relaxed mb-3">
                                <span className="text-primary font-semibold">
                                    {t("joinInstructor_transp_promise_split")}
                                </span>{" "}
                                {t("joinInstructor_transp_promise_desc")}
                            </p>
                        </div>
                        <p className="text-[10px] uppercase tracking-[0.2em] text-gray-500 dark:text-white/55 leading-relaxed mt-6 font-mono">
                            {t("joinInstructor_transp_promise_footer")}
                        </p>
                    </div>
                </RevealOnScroll>
            </div>
        </SectionShell>
    );
}
